/**
 * Product Add-ons Setup Widget
 *
 * Module 1: Dynamic Add-On Logic
 *   - Single-Box Grinder Color Syncing (variant:change → highlight matching grinder)
 *   - Dual-Box BOGO Grinder Display (track Box1 + Box2 colors, surface both grinders)
 *   - Kushii Variant Thumbnails (clickable color thumbs → hidden input update)
 *   - Single-Option Add-ons (OG Stash Jar, ZootCase — simple toggle, no variant UI)
 *
 * Module 2: UI Overlays & Modals
 *   - "Learn More" image pop-up modal on add-on thumbnails
 */
(function () {
  'use strict';

  var SEL = {
    widget: '[data-product-addons]',
    grinderTrack: '[data-grinder-track]',
    grinderCard: '[data-grinder-card]',
    grinderAddBtn: '[data-grinder-add]',
    grinderQtyMinus: '[data-grinder-qty-minus]',
    grinderQtyPlus: '[data-grinder-qty-plus]',
    grinderQtyValue: '[data-grinder-qty-value]',
    toggleSwitch: '[data-addon-toggle]',
    kushiiThumb: '[data-kushii-thumb]',
    kushiiVariantInput: '[data-kushii-variant-input]',
    slotSwatch: '[data-slot-swatch]',
    slotVariantInput: '[data-slot-variant-input]',
    slotExpand: '[data-slot-expand]',
    slotColorName: '[data-slot-color-name]',
    slotMainImg: '[data-slot-main-img]',
    carouselPrev: '[data-carousel-prev]',
    carouselNext: '[data-carousel-next]',
    summaryContainer: '[data-addons-summary]',
    learnMore: '[data-addon-learn-more]'
  };

  /* ---------- Add-on discount & color mapping ---------- */

  var ADDON_DISCOUNT = 0.80; // default, overridden per-widget from data attrs

  /* Stash Box color → Grinder color mapping */
  var COLOR_MAP = {
    'bamboo': 'black',
    'black': 'black',
    'blue': 'blue',
    'royal blue': 'blue',
    'purple': 'purple',
    'pink': 'pink',
    'red': 'red',
    'green': 'green',
    'neon green': 'green',
    'teal': 'teal',
    'yellow': 'yellow'
  };

  /* Neon colors with no matching grinder — skip auto-sync */
  var NEON_EXCEPTIONS = ['neon pink', 'neon orange'];

  /* ========================================================
     Module 1: ProductAddons — state & dynamic logic
     ======================================================== */
  function ProductAddons(el) {
    this.el = el;
    this.grinderTrack = el.querySelector(SEL.grinderTrack);

    // State
    this.selectedGrinders = {};      // { variantId: qty }
    this.toggledAddons = {};         // { variantId: { price, title } }
    this.kushiiSelectedVariant = null;
    this.boxColors = { box1: null, box2: null }; // BOGO tracker
    this.isBogo = false;
    this.basePriceCache = {};

    // Discount config from block settings
    this.enableDiscount = el.dataset.enableDiscount !== 'false';
    var discountPct = parseInt(el.dataset.discountPercent, 10);
    this.addonDiscount = this.enableDiscount ? (100 - (isNaN(discountPct) ? 20 : discountPct)) / 100 : 1;

    // Product data from Liquid JSON
    this.grinderData = jsonParse(el.dataset.grinderProduct);
    this.stashJarData = jsonParse(el.dataset.stashJarProduct);
    this.zootcaseData = jsonParse(el.dataset.zootcaseProduct);
    this.kushiiData = jsonParse(el.dataset.kushiiProduct);

    this.cacheBasePrices();
    this.detectBogoState();
    this.bindGrinderEvents();
    this.bindToggleEvents();
    this.bindKushiiEvents();
    this.bindSlotVariantEvents();
    this.bindCarouselEvents();
    this.listenForVariantChanges();
    this.interceptFormSubmission();
  }

  /* ---------- helpers ---------- */

  function jsonParse(str) {
    try { return JSON.parse(str || '{}'); } catch (e) { return {}; }
  }

  function parsePriceCents(s) {
    if (!s) return 0;
    return Math.round(parseFloat(s.replace(/[^0-9.]/g, '')) * 100) || 0;
  }

  function formatPrice(cents) {
    var d = cents / 100;
    return d % 1 === 0 ? d.toFixed(0) : d.toFixed(2);
  }

  function formatMoney(cents) {
    return '$' + formatPrice(cents);
  }

  /* ---------- init helpers ---------- */

  ProductAddons.prototype.cacheBasePrices = function () {
    var self = this;
    document.querySelectorAll('.hash-btn button').forEach(function (btn) {
      if (btn.dataset.finalPrice)
        self.basePriceCache['single'] = parsePriceCents(btn.dataset.finalPrice);
      if (btn.dataset.bogoFinalPrice)
        self.basePriceCache['bogo'] = parsePriceCents(btn.dataset.bogoFinalPrice);
    });
  };

  ProductAddons.prototype.detectBogoState = function () {
    // Check if BOGO tab is active
    var bogoRadio = document.querySelector('#b1g1_checked');
    if (bogoRadio && bogoRadio.checked) {
      this.isBogo = true;
    }
    // Also check visibility of BOGO form
    var bogoForm = document.querySelector('#b1g1_form');
    if (bogoForm && bogoForm.style.display !== 'none') {
      this.isBogo = true;
    }
  };

  /* ========================================================
     Module 1A: Grinder Card Events
     ======================================================== */

  ProductAddons.prototype.bindGrinderEvents = function () {
    var self = this;

    // Add buttons
    this.el.querySelectorAll(SEL.grinderAddBtn).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var card = btn.closest(SEL.grinderCard);
        self.addGrinder(card.dataset.variantId, card);
      });
    });

    // Qty minus
    this.el.querySelectorAll(SEL.grinderQtyMinus).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var card = btn.closest(SEL.grinderCard);
        self.removeGrinder(card.dataset.variantId, card);
      });
    });

    // Qty plus
    this.el.querySelectorAll(SEL.grinderQtyPlus).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var card = btn.closest(SEL.grinderCard);
        self.addGrinder(card.dataset.variantId, card);
      });
    });
  };

  ProductAddons.prototype.addGrinder = function (variantId, card) {
    this.selectedGrinders[variantId] = (this.selectedGrinders[variantId] || 0) + 1;
    this.renderGrinderCard(variantId, card);
    this.updateSummaryAndPrice();
  };

  ProductAddons.prototype.removeGrinder = function (variantId, card) {
    if (this.selectedGrinders[variantId]) {
      this.selectedGrinders[variantId]--;
      if (this.selectedGrinders[variantId] <= 0) delete this.selectedGrinders[variantId];
    }
    this.renderGrinderCard(variantId, card);
    this.updateSummaryAndPrice();
  };

  ProductAddons.prototype.renderGrinderCard = function (variantId, card) {
    var qty = this.selectedGrinders[variantId] || 0;
    var addBtn = card.querySelector('.product-addons__grinder-action-add');
    var qtyCtrl = card.querySelector('.product-addons__qty-controls');
    var qtyVal = card.querySelector(SEL.grinderQtyValue);

    if (qty > 0) {
      if (addBtn) addBtn.style.display = 'none';
      if (qtyCtrl) qtyCtrl.style.display = 'flex';
      if (qtyVal) qtyVal.textContent = qty;
      card.classList.add('product-addons__grinder-card--selected');
    } else {
      if (addBtn) addBtn.style.display = 'flex';
      if (qtyCtrl) qtyCtrl.style.display = 'none';
      card.classList.remove('product-addons__grinder-card--selected');
    }
  };

  /* ========================================================
     Module 1B: Single-Box Color Sync + BOGO Dual Display
     ======================================================== */

  ProductAddons.prototype.listenForVariantChanges = function () {
    var self = this;

    document.addEventListener('variant:change', function (e) {
      var detail = e.detail || {};
      var variant = detail.variant;
      var formId = detail.formId || '';
      if (!variant) return;

      // Extract color from variant option1 or public_title
      var color = self.extractColor(variant);
      if (!color) return;

      // Detect BOGO tab switches
      self.detectBogoState();

      if (self.isBogo) {
        // ---- BOGO MODE: Track two box colors ----
        if (formId === 'b1g1') {
          self.boxColors.box1 = color;
        } else if (formId === 'b1g1_top') {
          self.boxColors.box2 = color;
        }
        // Surface both matching grinders
        self.highlightBogoGrinders();
      } else {
        // ---- SINGLE BOX MODE: Sync one grinder ----
        self.boxColors.box1 = color;
        self.boxColors.box2 = null;
        self.highlightSingleGrinder(color);
      }
    });

    // Also listen for tab switches (single ↔ BOGO)
    var tabRadios = document.querySelectorAll('input[name="b1g1_selector"]');
    tabRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        self.detectBogoState();
        if (!self.isBogo) {
          self.boxColors.box2 = null;
          if (self.boxColors.box1) {
            self.highlightSingleGrinder(self.boxColors.box1);
          }
        }
      });
    });
  };

  ProductAddons.prototype.extractColor = function (variant) {
    // Use option1 first (typically the color option for stashbox)
    var color = (variant.option1 || '').toLowerCase().trim();
    if (color) return color;

    // Fallback: parse public_title
    var pt = (variant.public_title || variant.title || '').toLowerCase().trim();
    return pt || null;
  };

  ProductAddons.prototype.highlightSingleGrinder = function (color) {
    // Skip neon colors — no matching grinder
    if (NEON_EXCEPTIONS.indexOf(color) !== -1) return;

    // Map stash box color to grinder color, fall back to direct match
    var grinderColor = COLOR_MAP[color] || color;

    var cards = this.el.querySelectorAll(SEL.grinderCard);
    var matchCard = null;

    cards.forEach(function (card) {
      var cardColor = (card.dataset.color || '').toLowerCase();
      card.classList.remove('product-addons__grinder-card--suggested');
      if (cardColor === grinderColor) {
        matchCard = card;
      }
    });

    if (matchCard) {
      matchCard.classList.add('product-addons__grinder-card--suggested');
      this.scrollToCard(matchCard);
    }
  };

  ProductAddons.prototype.highlightBogoGrinders = function () {
    var box1Color = (this.boxColors.box1 || '').toLowerCase();
    var box2Color = (this.boxColors.box2 || '').toLowerCase();

    // Map stash box colors to grinder colors, skip neons
    var grinder1 = (NEON_EXCEPTIONS.indexOf(box1Color) === -1) ? (COLOR_MAP[box1Color] || box1Color) : null;
    var grinder2 = (NEON_EXCEPTIONS.indexOf(box2Color) === -1) ? (COLOR_MAP[box2Color] || box2Color) : null;

    var cards = this.el.querySelectorAll(SEL.grinderCard);
    var firstMatch = null;

    cards.forEach(function (card) {
      var cardColor = (card.dataset.color || '').toLowerCase();
      card.classList.remove('product-addons__grinder-card--suggested');

      if ((grinder1 && cardColor === grinder1) || (grinder2 && cardColor === grinder2)) {
        card.classList.add('product-addons__grinder-card--suggested');
        if (!firstMatch) firstMatch = card;
      }
    });

    if (firstMatch) {
      this.scrollToCard(firstMatch);
    }
  };

  ProductAddons.prototype.scrollToCard = function (card) {
    var carousel = this.el.querySelector('.product-addons__grinder-carousel');
    if (!carousel || !card) return;
    var cardLeft = card.offsetLeft - carousel.offsetLeft;
    var cardRight = cardLeft + card.offsetWidth;
    var visibleLeft = carousel.scrollLeft;
    var visibleRight = visibleLeft + carousel.clientWidth;
    // Only scroll if the card isn't already fully visible
    if (cardLeft < visibleLeft || cardRight > visibleRight) {
      carousel.scrollTo({ left: cardLeft, behavior: 'smooth' });
    }
  };

  /* ========================================================
     Module 1C: Toggle Add-ons (Stash Jar, ZootCase — no variants)
     ======================================================== */

  ProductAddons.prototype.bindToggleEvents = function () {
    var self = this;

    this.el.querySelectorAll(SEL.toggleSwitch).forEach(function (toggle) {
      toggle.addEventListener('change', function () {
        var row = toggle.closest('.product-addons__toggle-row');
        var vid = row.dataset.variantId;
        var price = parseInt(row.dataset.price, 10);
        var title = row.dataset.title;

        if (toggle.checked) {
          self.toggledAddons[vid] = { price: price, title: title };
          row.classList.add('product-addons__toggle-row--active');
        } else {
          delete self.toggledAddons[vid];
          row.classList.remove('product-addons__toggle-row--active');
        }
        self.updateSummaryAndPrice();
      });
    });
  };

  /* ========================================================
     Module 1D: Kushii Variant Thumbnails
     ======================================================== */

  ProductAddons.prototype.bindKushiiEvents = function () {
    var self = this;

    this.el.querySelectorAll(SEL.kushiiThumb).forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        var row = thumb.closest('.product-addons__toggle-row');
        var vid = thumb.dataset.variantId;
        var price = parseInt(thumb.dataset.price, 10);
        var title = thumb.dataset.title;

        // Visual selection
        row.querySelectorAll(SEL.kushiiThumb).forEach(function (t) {
          t.classList.remove('product-addons__kushii-thumb--selected');
        });
        thumb.classList.add('product-addons__kushii-thumb--selected');

        // Update hidden input
        var hidden = row.querySelector(SEL.kushiiVariantInput);
        if (hidden) hidden.value = vid;

        // Update row data attrs
        row.dataset.variantId = vid;
        row.dataset.price = price;
        row.dataset.title = title;

        // Update main image to match selected variant
        var mainImg = row.querySelector('[data-kushii-main-img]');
        if (mainImg && thumb.dataset.image) {
          mainImg.src = thumb.dataset.image;
          mainImg.alt = title;
          if (thumb.dataset.modalImage) {
            mainImg.dataset.modalImage = thumb.dataset.modalImage;
          }
          mainImg.dataset.modalTitle = title;
          mainImg.dataset.modalPrice = '+' + formatMoney(Math.round(price * self.addonDiscount));
        }
        // Also update the learn-more icon modal data
        var learnIcon = row.querySelector('.product-addons__learn-more-icon');
        if (learnIcon && thumb.dataset.modalImage) {
          learnIcon.dataset.modalImage = thumb.dataset.modalImage;
          learnIcon.dataset.modalTitle = title;
          learnIcon.dataset.modalPrice = '+' + formatMoney(Math.round(price * self.addonDiscount));
        }

        // Update price display
        var origEl = row.querySelector('.product-addons__price-original');
        var discEl = row.querySelector('.product-addons__price-discounted');
        if (self.enableDiscount) {
          if (origEl) origEl.textContent = formatMoney(price);
          if (discEl) discEl.textContent = '+' + formatMoney(Math.round(price * self.addonDiscount));
        } else {
          if (discEl) discEl.textContent = '+' + formatMoney(price);
        }

        // If toggle is on, swap tracked addon
        var toggle = row.querySelector(SEL.toggleSwitch);
        if (toggle && toggle.checked) {
          if (self.kushiiSelectedVariant) delete self.toggledAddons[self.kushiiSelectedVariant];
          self.toggledAddons[vid] = { price: price, title: title };
        }
        self.kushiiSelectedVariant = vid;
        self.updateSummaryAndPrice();
      });
    });
  };

  /* ========================================================
     Module 1E: Slot Variant Swatches (top-4 + expand)
     Handles any toggle-row slot that has [data-slot-swatch] children.
     ======================================================== */

  ProductAddons.prototype.bindSlotVariantEvents = function () {
    var self = this;

    // Swatch clicks
    this.el.querySelectorAll(SEL.slotSwatch).forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        e.preventDefault();
        var row = swatch.closest('.product-addons__toggle-row');
        var swatchesWrap = swatch.closest('[data-slot-swatches]');
        var vid = swatch.dataset.variantId;
        var price = parseInt(swatch.dataset.price, 10);
        var title = swatch.dataset.title;
        var color = swatch.dataset.color || '';

        // Visual selection
        swatchesWrap.querySelectorAll(SEL.slotSwatch).forEach(function (s) {
          s.classList.remove('product-addons__slot-swatch--selected');
        });
        swatch.classList.add('product-addons__slot-swatch--selected');

        // Update color name label
        var colorLabel = row.querySelector(SEL.slotColorName);
        if (colorLabel) colorLabel.textContent = color;

        // Update hidden variant input
        var hidden = swatchesWrap.querySelector(SEL.slotVariantInput);
        if (hidden) hidden.value = vid;

        // Update row data attrs (used by toggle + cart)
        row.dataset.variantId = vid;
        row.dataset.price = price;
        row.dataset.title = title;

        // Update main image
        var mainImg = row.querySelector(SEL.slotMainImg);
        if (mainImg && swatch.dataset.image) {
          mainImg.src = swatch.dataset.image;
          mainImg.alt = title;
          if (swatch.dataset.modalImage) {
            mainImg.dataset.modalImage = swatch.dataset.modalImage;
          }
          mainImg.dataset.modalTitle = title;
          mainImg.dataset.modalPrice = '+' + formatMoney(Math.round(price * self.addonDiscount));
        }

        // Update learn-more icon modal data
        var learnIcon = row.querySelector('.product-addons__learn-more-icon');
        if (learnIcon && swatch.dataset.modalImage) {
          learnIcon.dataset.modalImage = swatch.dataset.modalImage;
          learnIcon.dataset.modalTitle = title;
          learnIcon.dataset.modalPrice = '+' + formatMoney(Math.round(price * self.addonDiscount));
        }

        // Update price display
        var origEl = row.querySelector('.product-addons__price-original');
        var discEl = row.querySelector('.product-addons__price-discounted');
        if (self.enableDiscount) {
          if (origEl) origEl.textContent = formatMoney(price);
          if (discEl) discEl.textContent = '+' + formatMoney(Math.round(price * self.addonDiscount));
        } else {
          if (discEl) discEl.textContent = '+' + formatMoney(price);
        }

        // If toggle is on, swap tracked addon variant
        var toggle = row.querySelector(SEL.toggleSwitch);
        if (toggle && toggle.checked) {
          // Remove old variant entry (find by row's previous variantId)
          Object.keys(self.toggledAddons).forEach(function (k) {
            if (self.toggledAddons[k] && self.toggledAddons[k]._rowEl === row) {
              delete self.toggledAddons[k];
            }
          });
          self.toggledAddons[vid] = { price: price, title: title, _rowEl: row };
        }

        self.updateSummaryAndPrice();
      });
    });

    // Expand / collapse "+ X more" button
    this.el.querySelectorAll(SEL.slotExpand).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-slot-swatches]');
        // Save original label BEFORE changing text
        if (!btn.dataset.defaultLabel) {
          btn.dataset.defaultLabel = btn.textContent;
        }
        var expanded = wrap.classList.toggle('product-addons__slot-swatches--expanded');
        btn.textContent = expanded ? '▲ less' : (btn.dataset.defaultLabel || '+ more');
      });
    });

    // Init pass: sync price display for pre-selected swatches on page load
    this.el.querySelectorAll('[data-slot-swatches]').forEach(function (wrap) {
      var selected = wrap.querySelector('.product-addons__slot-swatch--selected');
      if (!selected) return;
      var row = selected.closest('.product-addons__toggle-row');
      if (!row) return;
      var price = parseInt(selected.dataset.price, 10);
      if (!price) return;

      // Update row data attrs
      row.dataset.variantId = selected.dataset.variantId;
      row.dataset.price = price;
      row.dataset.title = selected.dataset.title || '';

      // Update price display
      var origEl = row.querySelector('.product-addons__price-original');
      var discEl = row.querySelector('.product-addons__price-discounted');
      if (self.enableDiscount) {
        if (origEl) origEl.textContent = formatMoney(price);
        if (discEl) discEl.textContent = '+' + formatMoney(Math.round(price * self.addonDiscount));
      } else {
        if (discEl) discEl.textContent = '+' + formatMoney(price);
      }

      // Update color name label
      var colorLabel = row.querySelector(SEL.slotColorName);
      if (colorLabel) colorLabel.textContent = selected.dataset.color || '';
    });
  };

  /* ========================================================
     Carousel Navigation
     ======================================================== */

  ProductAddons.prototype.bindCarouselEvents = function () {
    var self = this;
    var prev = this.el.querySelector(SEL.carouselPrev);
    var next = this.el.querySelector(SEL.carouselNext);
    if (prev) prev.addEventListener('click', function () { self.scrollCarousel(-1); });
    if (next) next.addEventListener('click', function () { self.scrollCarousel(1); });
  };

  ProductAddons.prototype.scrollCarousel = function (dir) {
    var carousel = this.el.querySelector('.product-addons__grinder-carousel');
    if (!carousel) return;
    var scrollAmount = 152; // 140px card + 12px gap
    carousel.scrollBy({ left: dir * scrollAmount, behavior: 'smooth' });
  };

  /* ========================================================
     Price & Summary Updates
     ======================================================== */

  ProductAddons.prototype.getAddonTotal = function () {
    var total = 0;
    var variants = this.grinderData.variants || [];
    var self = this;

    Object.keys(this.selectedGrinders).forEach(function (vid) {
      var v = variants.find(function (x) { return String(x.id) === String(vid); });
      if (v) total += Math.round(v.price * self.addonDiscount) * self.selectedGrinders[vid];
    });

    Object.keys(this.toggledAddons).forEach(function (vid) {
      total += Math.round(self.toggledAddons[vid].price * self.addonDiscount);
    });

    return total;
  };

  ProductAddons.prototype.getAddonItems = function () {
    var items = [];
    var variants = this.grinderData.variants || [];
    var self = this;

    Object.keys(this.selectedGrinders).forEach(function (vid) {
      var v = variants.find(function (x) { return String(x.id) === String(vid); });
      if (v) {
        items.push({ id: parseInt(vid, 10), quantity: self.selectedGrinders[vid], title: 'Grinder ' + (v.title || ''), price: v.price });
      }
    });

    Object.keys(this.toggledAddons).forEach(function (vid) {
      var d = self.toggledAddons[vid];
      items.push({ id: parseInt(vid, 10), quantity: 1, title: d.title, price: d.price });
    });

    return items;
  };

  ProductAddons.prototype.updateSummaryAndPrice = function () {
    var items = this.getAddonItems();
    var total = this.getAddonTotal();

    // Summary text
    var el = this.el.querySelector(SEL.summaryContainer);
    if (el) {
      if (items.length > 0) {
        el.innerHTML = 'Add-ons: <strong>' + items.map(function (i) { return i.title; }).join(', ') + '</strong>';
        el.style.display = 'block';
      } else {
        el.style.display = 'none';
      }
    }

    // Dispatch event
    this.el.dispatchEvent(new CustomEvent('addons:change', {
      detail: { items: items, total: total },
      bubbles: true
    }));

    // Update ATC button price
    this.updateATCPrice(total);
  };

  ProductAddons.prototype.updateATCPrice = function (addonTotal) {
    var self = this;
    document.querySelectorAll('.hash-btn button').forEach(function (btn) {
      var priceEl = btn.querySelector('.discounted-price');
      if (!priceEl) return;

      var base = 0;
      if (btn.dataset.finalPrice) {
        base = self.basePriceCache['single'] || parsePriceCents(btn.dataset.finalPrice);
      } else if (btn.dataset.bogoFinalPrice) {
        base = self.basePriceCache['bogo'] || parsePriceCents(btn.dataset.bogoFinalPrice);
      }

      if (base > 0) {
        priceEl.textContent = ' $' + formatPrice(base + addonTotal) + ' ';
      }
    });
  };

  /* ========================================================
     Form Submission — bundle add-ons into cart
     ======================================================== */

  ProductAddons.prototype.interceptFormSubmission = function () {
    var self = this;
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      var urlStr = (typeof url === 'string') ? url : (url && url.url) || '';

      // Only intercept POST requests to /cart/add
      if (urlStr.indexOf('/cart/add') !== -1 && options && options.method &&
          options.method.toUpperCase() === 'POST') {
        var addonItems = self.getAddonItems();
        if (addonItems.length > 0) {
          // Try to inject addon items into JSON body
          if (options.body && typeof options.body === 'string') {
            try {
              var body = JSON.parse(options.body);
              if (body.items && Array.isArray(body.items)) {
                addonItems.forEach(function (item) {
                  body.items.push({ id: item.id, quantity: item.quantity, properties: { '_addon_bundle': 'true' } });
                });
                var newOptions = Object.assign({}, options, { body: JSON.stringify(body) });
                return originalFetch.call(this, url, newOptions);
              }
            } catch (e) { /* not JSON, fall through */ }
          }

          // Fallback (FormData or non-items body): chain a second request after the main one
          return originalFetch.apply(this, arguments).then(function (res) {
            if (res.ok) {
              return originalFetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: addonItems.map(function (i) { return { id: i.id, quantity: i.quantity, properties: { '_addon_bundle': 'true' } }; })
                })
              }).then(function () { return res; });
            }
            return res;
          });
        }
      }
      return originalFetch.apply(this, arguments);
    };
  };

  /* ========================================================
     Module 2: "Learn More" Image Pop-up Modal
     ======================================================== */

  function initModal() {
    // Create modal DOM once
    var overlay = document.createElement('div');
    overlay.className = 'product-addons-modal__overlay';
    overlay.innerHTML =
      '<div class="product-addons-modal__content">' +
        '<button type="button" class="product-addons-modal__close" aria-label="Close">&times;</button>' +
        '<img class="product-addons-modal__image" src="" alt="">' +
        '<div class="product-addons-modal__details">' +
          '<h3 class="product-addons-modal__title"></h3>' +
          '<p class="product-addons-modal__price"></p>' +
          '<p class="product-addons-modal__desc"></p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);

    var modalImg = overlay.querySelector('.product-addons-modal__image');
    var modalTitle = overlay.querySelector('.product-addons-modal__title');
    var modalPrice = overlay.querySelector('.product-addons-modal__price');
    var modalDesc = overlay.querySelector('.product-addons-modal__desc');
    var closeBtn = overlay.querySelector('.product-addons-modal__close');

    function openModal(imgSrc, title, price, desc) {
      modalImg.src = imgSrc || '';
      modalImg.alt = title || '';
      modalTitle.textContent = title || '';
      modalPrice.textContent = price || '';
      modalDesc.innerHTML = desc || '';
      overlay.classList.add('product-addons-modal__overlay--active');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      overlay.classList.remove('product-addons-modal__overlay--active');
      document.body.style.overflow = '';
    }

    // Close on X button
    closeBtn.addEventListener('click', closeModal);

    // Close on overlay click (outside content)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });

    // Bind "learn more" triggers
    document.querySelectorAll(SEL.learnMore).forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var data = trigger.dataset;
        openModal(data.modalImage, data.modalTitle, data.modalPrice, data.modalDesc);
      });
    });

    // Prevent clicks inside modal content from bubbling to document-level close handlers
    overlay.querySelector('.product-addons-modal__content').addEventListener('click', function (e) {
      e.stopPropagation();
    });
  }

  /* ========================================================
     Init
     ======================================================== */

  function init() {
    document.querySelectorAll('[data-product-addons]').forEach(function (widget) {
      widget._addonsInstance = new ProductAddons(widget);
    });
    initModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
